import { useState } from "react";
import { Link } from "react-router";
import { ArrowLeft, Loader2, Palette, Printer } from "lucide-react";
import { CatalogoDoLaboratorioDocumento } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { useSetPageHeader } from "@/lib/page-header";
import { usePermissao } from "@/lib/use-permissao";
import { cn } from "@/lib/cn";
import { PainelDeAparencia } from "@/components/catalogo/aparencia/PainelDeAparencia";
import { useAparenciaDoCatalogo } from "@/components/catalogo/aparencia/use-aparencia-do-catalogo";

// Na impressão só o documento aparece: menu, barra e botões somem e o catálogo ocupa a folha.
const CSS_DE_IMPRESSAO = `
@media print {
  body * { visibility: hidden !important; }
  [data-catalogo-impresso], [data-catalogo-impresso] * { visibility: visible !important; }
  [data-catalogo-impresso] { position: absolute; inset: 0 auto auto 0; width: 100%; padding: 0 !important; border: 0 !important; box-shadow: none !important; }
}`;

/** O catálogo como documento: o que vai no PDF ("Baixar PDF" = imprimir e salvar como PDF) e é o
 *  mesmo que o dentista vê pelo link público/QR. */
export function CatalogoVisualizar() {
  useSetPageHeader({ title: "Visualização do catálogo", subtitle: "Como o dentista vê o seu catálogo, pronto para virar PDF" }, []);
  const podeVerPrecos = trpc.catalogoPublico.podeVerPrecos.useQuery().data ?? false;
  const [comPreco, setComPreco] = useState(true);
  const catalogo = trpc.catalogoPublico.visualizar.useQuery({ comPreco: comPreco && podeVerPrecos });
  const podePersonalizar = usePermissao("settings.manage");
  const [personalizando, setPersonalizando] = useState(false);
  const ed = useAparenciaDoCatalogo(podePersonalizar && personalizando);
  // Personalizando, a pré-visualização é o rascunho — o mesmo documento que vai pro PDF.
  const exibido =
    catalogo.data && personalizando && ed.rascunho ? { ...catalogo.data, aparencia: ed.rascunho, capaUrl: ed.capaUrl } : catalogo.data;

  return (
    <div className={cn("mx-auto w-full space-y-4", personalizando ? "max-w-[1400px]" : "max-w-4xl")}>
      <style>{CSS_DE_IMPRESSAO}</style>
      <div className="flex flex-wrap items-center gap-3">
        <Link
          to="/lab/catalogo"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Catálogo
        </Link>
        <div className="ml-auto flex items-center gap-2">
          {podeVerPrecos && (
            <div className="inline-flex rounded-md border border-zinc-200 bg-white p-0.5 text-sm dark:border-border dark:bg-card">
              {[true, false].map((v) => (
                <button
                  key={String(v)}
                  type="button"
                  onClick={() => setComPreco(v)}
                  aria-pressed={comPreco === v}
                  className={
                    comPreco === v
                      ? "rounded px-3 py-1.5 font-semibold text-white bg-brand-600"
                      : "rounded px-3 py-1.5 text-zinc-600 hover:bg-zinc-50 dark:text-muted-foreground dark:hover:bg-muted"
                  }
                >
                  {v ? "Com preços" : "Sem preços"}
                </button>
              ))}
            </div>
          )}
          {podePersonalizar && (
            <button
              type="button"
              onClick={() => setPersonalizando((v) => !v)}
              aria-pressed={personalizando}
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 text-sm font-semibold text-zinc-700 hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted"
            >
              <Palette className="h-4 w-4" />
              {personalizando ? "Fechar personalização" : "Personalizar"}
            </button>
          )}
          <button
            type="button"
            onClick={() => window.print()}
            disabled={!catalogo.data}
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
          >
            <Printer className="h-4 w-4" />
            Baixar PDF
          </button>
        </div>
      </div>

      <div className={cn("grid items-start gap-4", personalizando && "lg:grid-cols-[minmax(0,1fr)_340px]")}>
        {personalizando && (
          <div className="lg:sticky lg:top-4 lg:order-2">
            <PainelDeAparencia ed={ed} temLogo={!!catalogo.data?.laboratorio.logoUrl} />
          </div>
        )}
        <div data-catalogo-impresso className="min-w-0 rounded-xl border border-zinc-200 bg-white p-6 shadow-sm sm:p-10 dark:border-border">
          {exibido ? (
            <CatalogoDoLaboratorioDocumento catalogo={exibido} />
          ) : (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
            </div>
          )}
        </div>
      </div>
      <p className="text-center text-xs text-zinc-500 dark:text-muted-foreground">
        Em Baixar PDF, escolha "Salvar como PDF" como destino da impressão.
      </p>
    </div>
  );
}
